/**
 * CREATE-prefix consumption + comment-deaf paren/comma helpers for the
 * parked-routine parser (VDE-2 Slice 4). Split out of `routine.parse.ts`
 * alongside `routine.parse.lex.ts` to keep each module under the 200-line
 * file budget (ROUND2-MINOR-14) while preserving the byte-faithful contract
 * documented on `routine.parse.ts`.
 *
 * Regex-free (project rule VQE-D10). Do NOT swap in the shared
 * `findMatchingParen` helper: it is comment- and engine-aware and would break
 * the required byte-parity (see the long note at the top of the original
 * `routine.parse.ts`).
 */

import {
  consumeWord,
  isWordChar,
  isWs,
  skipSpacesLocal,
} from './routine.parse.lex';

/** Consume `OR\s+<second>\b` anchored at `i` (the removed parser ran
 *  `^OR\s+REPLACE\b` / `^OR\s+ALTER\b` against `trimmed.slice(cursor)`, so
 *  `OR` itself is NOT word-bounded on its left — the `^` anchor + the
 *  preceding `skipSpaces` make that immaterial here). Returns the index just
 *  past `<second>`, or `-1`. */
function consumeOrPhrase(source: string, i: number, end: number, second: string): number {
  if (source.slice(i, i + 2).toUpperCase() !== 'OR') return -1;
  const ws = skipSpacesLocal(source, i + 2, end);
  if (ws === i + 2) return -1; // \s+
  if (source.slice(ws, ws + second.length).toUpperCase() !== second) return -1;
  const after = ws + second.length;
  const next = source[after];
  if (next !== undefined && after < end && isWordChar(next)) return -1; // \b
  return after;
}

/** Consume mysql `DEFINER\s*=\s*[^,\s]+` anchored at `i` (the removed
 *  parser's prefix-skip for `DEFINER = '…'`). Returns the index just past the
 *  definer value, or `-1`. */
function consumeDefiner(source: string, i: number, end: number): number {
  if (source.slice(i, i + 7).toUpperCase() !== 'DEFINER') return -1;
  let p = skipSpacesLocal(source, i + 7, end); // \s*
  if (source[p] !== '=') return -1;
  p = skipSpacesLocal(source, p + 1, end); // \s*
  let q = p;
  while (q < end && !isWs(source[q]) && source[q] !== ',') q++; // [^,\s]+
  if (q === p) return -1;
  return q;
}

export interface PrefixState {
  cursor: number;
  orReplace: boolean;
  mssqlOrAlter: boolean;
  oracleEditionable: boolean;
  oracleNonEditionable: boolean;
}

/** CREATE-only prefix consumption — bounded 6-iteration loop faithful to the
 *  removed parser's `consumePrefixes` (`OR REPLACE` / `OR ALTER` /
 *  `EDITIONABLE` / `NONEDITIONABLE` / mysql `DEFINER = …`). */
export function consumePrefixes(source: string, start: number, end: number): PrefixState {
  let cursor = start;
  let orReplace = false;
  let mssqlOrAlter = false;
  let oracleEditionable = false;
  let oracleNonEditionable = false;
  for (let iter = 0; iter < 6; iter++) {
    cursor = skipSpacesLocal(source, cursor, end);
    const orRep = consumeOrPhrase(source, cursor, end, 'REPLACE');
    if (orRep !== -1) { orReplace = true; cursor = orRep; continue; }
    const orAlt = consumeOrPhrase(source, cursor, end, 'ALTER');
    if (orAlt !== -1) { mssqlOrAlter = true; cursor = orAlt; continue; }
    const ed = consumeWord(source, cursor, end, 'EDITIONABLE');
    if (ed !== -1) { oracleEditionable = true; cursor = ed; continue; }
    const ned = consumeWord(source, cursor, end, 'NONEDITIONABLE');
    if (ned !== -1) { oracleNonEditionable = true; cursor = ned; continue; }
    if (consumeWord(source, cursor, end, 'DEFINER') !== -1) {
      const def = consumeDefiner(source, cursor, end);
      if (def !== -1) { cursor = def; continue; }
    }
    break;
  }
  return { cursor, orReplace, mssqlOrAlter, oracleEditionable, oracleNonEditionable };
}

/** Comment-deaf paren matcher — replicates the removed parser's LOCAL
 *  `findMatchingParen` (depth count from the opening `(`, ignores strings /
 *  comments / quotes entirely, unlike the shared comment-aware one). Returns
 *  the index of the balancing `)`, or `-1`. */
export function findMatchingParenLocal(source: string, openIdx: number, end: number): number {
  let depth = 0;
  for (let i = openIdx; i < end; i++) {
    const ch = source[i];
    if (ch === '(') depth++;
    else if (ch === ')') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

/** Split at top-level commas, paren-depth tracked — replicates the removed
 *  parser's LOCAL `splitTopLevelCommas` byte-for-byte. A type like
 *  `NUMERIC(10,2)` keeps its inner comma.
 *
 *  Deliberately string-deaf AND non-clamping: a `(`/`)`/`,` inside a string
 *  literal (e.g. `DEFAULT ', '`, `@p NVARCHAR(MAX) = N')'`) mis-tracks depth
 *  and `depth` may go negative on unbalanced `)`. This is a faithfully copied
 *  removed-parser quirk — parity with the removed parser is the bar. Do NOT
 *  "fix" it (string-awareness or a `depth > 0` clamp) — that would change the
 *  split and break the verified byte-parity. */
export function splitTopLevelCommas(s: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === '(') depth++;
    else if (ch === ')') depth--;
    else if (ch === ',' && depth === 0) {
      out.push(s.slice(start, i));
      start = i + 1;
    }
  }
  out.push(s.slice(start));
  return out;
}
